// src/components/padrekko/PadRoadDrawer.jsx
//
// Coordinates MlDraw for user-drawn access roads. Watches
// usePadStore.drawingRoad — when it flips true, starts a LineString draw
// via draw.startLine(). When the user finishes (right-click → completeLine
// → _commit → onUpdate), the resulting geometry is handed to
// store.addUserRoad and the drawing flag is reset.
//
// Uses the existing MlDraw instance owned by MapView, same as AOI drawing.

import { useEffect, useRef } from "react";
import { usePadStore } from "../../stores/usePadStore";

export default function PadRoadDrawer({ drawInstance }) {

  // Only subscribe to state we actually need to react to.
  // Actions are pulled via getState() inside the effect — Zustand actions
  // are stable references but subscribing as deps risks spurious effect
  // re-runs if React-DevTools or HMR observes the store.
  const drawingRoad = usePadStore(s => s.drawingRoad);

  // Track which deps actually change so we can spot spurious effect re-runs
  const depsRef = useRef({});

  useEffect(() => {
    const prev = depsRef.current;
    const changed = {};
    if (prev.drawInstance !== drawInstance) changed.drawInstance = true;
    if (prev.drawingRoad  !== drawingRoad)  changed.drawingRoad  = true;
    // console.log('[PadRoadDrawer] deps changed:', changed);
    depsRef.current = { drawInstance, drawingRoad };

    if (!drawInstance) return;
    if (!drawingRoad) return;

    // Pull actions via getState — stable references, no need to subscribe
    const { addUserRoad, setDrawingRoad } = usePadStore.getState();

    // Snapshot the prior onUpdate so we can chain to it (MapPage uses it
    // to bump drawVersion) and restore it on cleanup.
    const prevOnUpdate = drawInstance.onUpdate;

    const handleUpdate = () => {
      prevOnUpdate?.();

      const f = drawInstance.lastFeature;
      if (!f || f.geometry?.type !== "LineString") return;

      // Capture geometry, clear the final feature, exit drawing mode
      addUserRoad(f.geometry);
      drawInstance.delete?.();
      drawInstance.stopDrawing?.();
      setDrawingRoad(false);
    };

    drawInstance.onUpdate = handleUpdate;

    // Start the draw. Clear any prior state first to avoid carry-over
    // from an AOI session.
    drawInstance.stopDrawing?.();
    drawInstance.delete?.();
    drawInstance.startLine?.();

    return () => {
      drawInstance.onUpdate = prevOnUpdate ?? null;

      // Only stop if we're still mid-draw. If the user already completed
      // a line (mode flipped to null via _commit), there's nothing to stop
      // and stopDrawing would just call _resetCursor unnecessarily.
      if (drawInstance.mode === "line") {
        drawInstance.stopDrawing?.();
      }
    };
  }, [drawInstance, drawingRoad]);

  return null;
}
